import type {ColorMap, CSS} from '@pinecast/styles';
import {focusVisibleRing, useColors, useCSS} from '@pinecast/styles';
import * as React from 'react';
import {Link as RouterLink} from 'react-router';
import type {JSX} from 'react';

import {DEFAULT_FONT} from './constants';
import Group from './Group';
import type {CommonProps} from './icons/types';
import type {ShortcutKey} from './KeyboardShortcut';
import KeyboardShortcut from './KeyboardShortcut';
import Spinner from './Spinner';

const spinnerStyle: React.CSSProperties = {
  bottom: 0,
  left: 0,
  margin: 'auto',
  position: 'absolute',
  right: 0,
  top: 0,
};

const buttonShadow = '0 1px 0 rgba(0, 0, 0, 0.15)';
const buttonShadowActive = '0 0 0 rgba(0, 0, 0, 0.05)';

const buttonStyles = (
  {
    disabled,
    $isBlock,
    $isPrimary,
    $pending,
    $size = 'normal',
  }: {
    disabled?: boolean;
    $isBlock?: boolean;
    $isPrimary?: boolean;
    $pending?: boolean;
    $size?: 'normal' | 'large' | 'small';
  },
  designColors: ColorMap,
) => ({
  backgroundColor: $isPrimary
    ? designColors.accentBlue
    : designColors.contentBackground,
  borderColor: $isPrimary
    ? disabled && !$pending
      ? designColors.borderGrayNormal
      : designColors.inputBorder
    : designColors.borderGrayNormal,
  borderStyle: disabled && !$isPrimary && !$pending ? 'dashed' : 'solid',
  borderWidth: $isPrimary ? 0 : '1px',
  borderRadius: '6px',
  boxShadow: buttonShadow,
  color: $pending
    ? 'transparent'
    : disabled
      ? designColors.borderGrayNormal
      : $isPrimary
        ? '#fff' // Always white, regardless of dark mode
        : designColors.contentText,
  cursor: 'pointer',
  display: $isBlock ? 'flex' : 'inline-flex',
  fontFamily: DEFAULT_FONT,
  fontSize: $size === 'normal' ? '14px' : $size === 'small' ? '12px' : '16px',
  height: $size === 'normal' ? '30px' : $size === 'small' ? '24px' : '36px',
  justifyContent: 'center',
  lineHeight:
    $size === 'normal'
      ? $isPrimary
        ? '31px'
        : '29px'
      : $size === 'small'
        ? '24px'
        : '34px',
  marginBottom: $isBlock ? '20px' : 0,
  marginLeft: 0,
  marginRight: 0,
  marginTop: 0,
  padding:
    $size === 'normal' ? '0 16px' : $size === 'small' ? '0 12px' : '0 20px',
  pointerEvents: $pending || disabled ? 'none' : undefined,
  position: 'relative',
  textDecoration: 'none',
  transform: 'translateY(0)',
  transition:
    'box-shadow 0.2s, border-color 0.2s, color 0.2s, transform 0.0625s',
  userSelect: 'none',
  whiteSpace: 'nowrap',

  ':hover': {
    // border: `1px solid ${
    //   $isPrimary ? designColors.borderGrayAccent : designColors.borderBlueNormal
    // }`,
    boxShadow: `${buttonShadow}, inset 0 0 0 2px ${
      $isPrimary ? designColors.inputBorder : designColors.borderBlueLight
    }`,
    textDecoration: 'none',
  },
  ':active': {
    boxShadow: `${buttonShadowActive}, inset 0 0 0 2px ${designColors.borderBlueNormal}`,
    transform: 'translateY(1px)',
  },

  // Outside the button, with a gap. A primary button's ring matches its fill,
  // so the ring meets only the surface.
  ...focusVisibleRing(designColors, 2, $isPrimary ? 'accent' : 'default'),
});

type Props = {
  'aria-controls'?: string;
  'aria-expanded'?: boolean;
  'aria-label'?: string;
  autoFocus?: boolean;
  children: React.ReactNode;
  className?: string;
  // Takes focus when the rows of a list are gone (see `FocusRecoveryProvider`). A
  // string names the focus group of that list. With `true`, the button takes
  // focus for every list around it.
  'data-focus-fallback'?: boolean | string;
  disabled?: boolean;
  download?: string;
  href?: string;
  icon?: React.ComponentType<CommonProps>;
  iconProps?: Partial<CommonProps>;
  $isBlock?: boolean;
  $isPrimary?: boolean;
  onClick?: () => void;
  onMouseDown?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => void;
  onMouseUp?: (
    e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
  ) => void;
  pending?: boolean;
  ref?: React.Ref<HTMLButtonElement | HTMLAnchorElement>;
  shortcut?: ShortcutKey;
  size?: 'normal' | 'small' | 'large';
  style?: CSS;
  tabIndex?: number;
  target?: '_blank';
  title?: string;
  type?: 'submit' | 'button';
};

const Button = ({
  'aria-controls': ariaControls,
  'aria-expanded': ariaExpanded,
  'aria-label': ariaLabel,
  $isBlock = false,
  $isPrimary = false,
  autoFocus,
  children,
  className,
  'data-focus-fallback': focusFallback,
  disabled = false,
  download,
  href,
  icon: Icon,
  iconProps,
  onClick,
  onMouseDown,
  onMouseUp,
  pending,
  ref,
  shortcut,
  size = 'normal',
  style,
  tabIndex,
  target,
  title,
  type = 'button',
}: Props) => {
  const css = useCSS();
  const designColors = useColors();
  const handleClick = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
      e.preventDefault();
      onClick!();
    },
    [onClick],
  );

  let Component: any;
  const extraProps: Record<string, any> = {};
  if (href) {
    if (
      !href.includes('://') &&
      !href.startsWith('data:') &&
      !target &&
      !download
    ) {
      Component = RouterLink;
      extraProps.to = href;
    } else {
      Component = 'a';
      extraProps.download = download;
      extraProps.href = href;
      extraProps.target = target;
    }
  } else {
    // Without a destination it is a button, even with no `onClick`: a submit
    // button, a disabled or pending placeholder, or a button whose press is
    // handled by `onMouseDown` or an ancestor. An `<a>` with no `href` has no
    // role and cannot take focus.
    Component = 'button';
    extraProps.autoFocus = autoFocus;
    extraProps.disabled = disabled;
  }

  return (
    <Component
      aria-controls={ariaControls}
      aria-expanded={ariaExpanded}
      aria-label={ariaLabel}
      data-autofocus={autoFocus} // React does not set `autofocus` in the dom, which makes selecting autofocused elements impossible
      data-focus-fallback={focusFallback || undefined}
      className={css(
        {
          ...buttonStyles(
            {
              disabled: disabled || pending,
              $isPrimary,
              $isBlock,
              $pending: pending,
              $size: size,
            },
            designColors,
          ),
          ...style,
        },
        className,
      )}
      onClick={onClick && handleClick}
      onMouseDown={onMouseDown}
      onMouseUp={onMouseUp}
      ref={ref}
      rel={href && href.match(/^\w+:\/\//) ? 'noopener noreferrer' : undefined}
      tabIndex={tabIndex}
      title={title}
      to={href as any}
      type={type}
      {...extraProps}
    >
      {pending && (
        <Spinner style={spinnerStyle} type={$isPrimary ? 'white' : undefined} />
      )}
      {Icon && (
        <Icon
          {...iconProps}
          size={size === 'normal' ? 12 : size === 'small' ? 12 : 16}
          style={{marginRight: 4, alignSelf: 'center'}}
        />
      )}
      {children}
      {shortcut ? (
        <KeyboardShortcut
          {...shortcut}
          style={{
            alignSelf: 'center',
            fontSize: '10px',
            fontWeight: 400,
            lineHeight: '1em',
            marginLeft: 4,
            opacity: 0.5,
            marginBottom: '-2px',
          }}
        />
      ) : null}
    </Component>
  );
};

export default Button;

export const ButtonGroup = ({
  children,
  style,
}: {
  children: Array<JSX.Element | false | null>;
  style?: CSS;
}) => (
  <Group spacing={8} wrapperStyle={style}>
    {children}
  </Group>
);
